import { useState } from 'react';
import axios from 'axios';
import { Key } from '@phosphor-icons/react';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { passwordProblem } from '../pages/Register';

// Shown after signing in with a temporary or administrator-issued password.
// The dialog cannot be dismissed until a new password has been set.
export default function ForcePasswordChange() {
  const { user, checkAuth, logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  if (!user || !user.must_change_password) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next !== confirm) { setError('New passwords do not match'); return; }
    const problem = passwordProblem(next);
    if (problem) { setError(problem); return; }
    setSaving(true);
    try {
      await axios.post('/api/auth/change-password', { current_password: current, new_password: next }, { withCredentials: true });
      await checkAuth();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open>
      <DialogContent className="max-w-md [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Key size={22} weight="duotone" className="text-blue-600" />Choose a new password</DialogTitle>
          <DialogDescription>You signed in with a temporary password. Please set your own password to continue.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="force-password-form">
          <Input type="password" placeholder="Current (temporary) password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
          <Input type="password" placeholder="New password (10+ characters, letters and numbers)" value={next} onChange={(e) => setNext(e.target.value)} required />
          <Input type="password" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">{error}</div>}
          <div className="flex gap-3">
            <Button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">{saving ? 'Saving...' : 'Set Password'}</Button>
            <Button type="button" variant="outline" className="rounded-lg" onClick={logout}>Sign out</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
